import { useRef, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { ColorSet, CompanyType, emailSafeFonts } from "../../models/company";
import { uploadCompanyFile } from "../../utils/http/companies";

// Custom font picker that renders each option in its own typeface with a description subtitle
function FontSelect({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = emailSafeFonts.find((f) => f.value === value);

  return (
    <div style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          width: "100%",
          textAlign: "left",
          background: disabled ? "#e9ecef" : "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          padding: "6px 32px 6px 10px",
          cursor: disabled ? "default" : "pointer",
          position: "relative",
          minHeight: 52,
        }}
      >
        {selected ? (
          <>
            <div style={{ fontFamily: selected.value, fontWeight: 500, fontSize: 14, lineHeight: 1.3 }}>
              {selected.label}
            </div>
            <div style={{ fontSize: 11, color: "#6c757d", marginTop: 1 }}>{selected.description}</div>
          </>
        ) : (
          <span style={{ color: "#6c757d", fontSize: 13 }}>{placeholder || "-- Select --"}</span>
        )}
        <i
          className={`bi bi-chevron-${open ? "up" : "down"}`}
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#6c757d", fontSize: 11 }}
        />
      </button>

      {/* Dropdown list */}
      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              zIndex: 1000,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {emailSafeFonts.map((font) => (
              <button
                key={font.value}
                type="button"
                onClick={() => { onChange(font.value); setOpen(false); }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  background: font.value === value ? "#f0f4ff" : "transparent",
                  border: "none",
                  borderBottom: "1px solid #f0f0f0",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => { if (font.value !== value) e.currentTarget.style.background = "#f8f9fa"; }}
                onMouseLeave={(e) => { if (font.value !== value) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ fontFamily: font.value, fontWeight: 500, fontSize: 14, lineHeight: 1.3 }}>
                  {font.label}
                  {font.value === value && (
                    <i className="bi bi-check2" style={{ marginLeft: 6, color: "#0049ff", fontSize: 12 }} />
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#6c757d", marginTop: 1 }}>{font.description}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface CompanyBrandingProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  setCompany: (company: CompanyType) => void;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

const COLOR_GROUPS: { key: "primary" | "secondary" | "accent"; label: string; description: string }[] = [
  { key: "primary", label: "Primary Colors", description: "Email background, header bar, main CTA button" },
  { key: "secondary", label: "Secondary Colors", description: "Content area background, body text, in-text links" },
  { key: "accent", label: "Accent Colors", description: "Callout blocks, badges, highlight links" },
];

const COLOR_FIELDS: { key: keyof ColorSet; label: string }[] = [
  { key: "background", label: "Background" },
  { key: "text", label: "Text" },
  { key: "link", label: "Link" },
];

// Colibry brand defaults
const DEFAULT_COLORS = {
  primary: { background: "#0049ff", text: "#ffffff", link: "#ffffff", links_underlined: true },
  secondary: { background: "#ffffff", text: "#1a2236", link: "#0049ff", links_underlined: true },
  accent: { background: "#ffaa00", text: "#1a2236", link: "#0049ff", links_underlined: true },
};

const DEFAULT_TYPOGRAPHY = {
  font_family: "Arial, sans-serif",
  heading_font: "Arial, sans-serif",
  font_size_body: 14,
  font_size_heading: 22,
  line_height: 1.6,
};

function CompanyBranding({
  company,
  originalCompany,
  setCompany,
  hasFieldChanged,
  ChangedIndicator,
  readOnly = false,
}: CompanyBrandingProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);

  const rawDesignTemp = company.config_design_temp || {};
  const rawOriginalDesignTemp = originalCompany?.config_design_temp || {};

  // Normalize both with defaults so comparisons don't flag default-filled values as changes.
  // Use `val || fallback` for strings (treats null/"" as missing) and `?? fallback` for numbers/booleans.
  const normalize = (d: typeof rawDesignTemp) => ({
    ...d,
    colors: (["primary", "secondary", "accent"] as const).reduce(
      (acc, group) => ({
        ...acc,
        [group]: {
          background: d.colors?.[group]?.background || DEFAULT_COLORS[group].background,
          text: d.colors?.[group]?.text || DEFAULT_COLORS[group].text,
          link: d.colors?.[group]?.link || DEFAULT_COLORS[group].link,
          links_underlined: d.colors?.[group]?.links_underlined != null ? d.colors[group].links_underlined : DEFAULT_COLORS[group].links_underlined,
        },
      }),
      {} as typeof DEFAULT_COLORS,
    ),
    typography: {
      font_family: d.typography?.font_family || DEFAULT_TYPOGRAPHY.font_family,
      heading_font: d.typography?.heading_font || DEFAULT_TYPOGRAPHY.heading_font,
      font_size_body: d.typography?.font_size_body ?? DEFAULT_TYPOGRAPHY.font_size_body,
      font_size_heading: d.typography?.font_size_heading ?? DEFAULT_TYPOGRAPHY.font_size_heading,
      line_height: d.typography?.line_height ?? DEFAULT_TYPOGRAPHY.line_height,
    },
  });

  const designTemp = normalize(rawDesignTemp);
  const originalDesignTemp = normalize(rawOriginalDesignTemp);

  // Remove keys with undefined/empty-string values and prune empty objects
  // so they don't get stored as empty in the DB.
  const stripEmpty = (obj: any): any => {
    if (obj === null || typeof obj !== "object" || Array.isArray(obj)) return obj;
    const cleaned = Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== "")
        .map(([k, v]) => [k, typeof v === "object" && v !== null && !Array.isArray(v) ? stripEmpty(v) : v])
        .filter(([, v]) => !(typeof v === "object" && v !== null && !Array.isArray(v) && Object.keys(v).length === 0)),
    );
    return cleaned;
  };

  const updateDesignTemp = (patch: Partial<typeof rawDesignTemp>) => {
    setCompany({
      ...company,
      config_design_temp: stripEmpty({
        ...rawDesignTemp,
        ...patch,
      }),
    });
  };

  const updateColor = (group: "primary" | "secondary" | "accent", field: keyof ColorSet, value: string | boolean) => {
    updateDesignTemp({
      colors: {
        ...rawDesignTemp.colors,
        [group]: {
          ...rawDesignTemp.colors?.[group],
          [field]: value === "" ? undefined : value,
        },
      },
    });
  };

  const updateTypography = (field: string, value: string | number) => {
    updateDesignTemp({
      typography: {
        ...rawDesignTemp.typography,
        [field]: value === "" ? undefined : value,
      },
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) processLogoFile(file);
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processLogoFile(file);
  };

  const processLogoFile = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2MB. Please choose a smaller image.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }
    setLogoUploading(true);
    setLogoUploadError(null);
    try {
      const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
      const renamedFile = new File([file], `logo${ext}`, { type: file.type });
      const result = await uploadCompanyFile({ companyNameOrId: company.name, file: renamedFile });
      updateDesignTemp({ images: { ...rawDesignTemp.images, logo: `${result.public_url}?t=${Date.now()}` } });
    } catch (e: any) {
      setLogoUploadError(e?.info?.detail || "Upload failed. Please try again.");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  // Effective values with Colibry defaults as fallback
  const ec = {
    primary: {
      bg: designTemp.colors?.primary?.background || DEFAULT_COLORS.primary.background,
      text: designTemp.colors?.primary?.text || DEFAULT_COLORS.primary.text,
      link: designTemp.colors?.primary?.link || DEFAULT_COLORS.primary.link,
      linksUnderlined: designTemp.colors?.primary?.links_underlined ?? DEFAULT_COLORS.primary.links_underlined,
    },
    secondary: {
      bg: designTemp.colors?.secondary?.background || DEFAULT_COLORS.secondary.background,
      text: designTemp.colors?.secondary?.text || DEFAULT_COLORS.secondary.text,
      link: designTemp.colors?.secondary?.link || DEFAULT_COLORS.secondary.link,
      linksUnderlined: designTemp.colors?.secondary?.links_underlined ?? DEFAULT_COLORS.secondary.links_underlined,
    },
    accent: {
      bg: designTemp.colors?.accent?.background || DEFAULT_COLORS.accent.background,
      text: designTemp.colors?.accent?.text || DEFAULT_COLORS.accent.text,
      link: designTemp.colors?.accent?.link || DEFAULT_COLORS.accent.link,
      linksUnderlined: designTemp.colors?.accent?.links_underlined ?? DEFAULT_COLORS.accent.links_underlined,
    },
  };

  const et = {
    bodyFont: designTemp.typography?.font_family || DEFAULT_TYPOGRAPHY.font_family,
    headingFont: designTemp.typography?.heading_font || DEFAULT_TYPOGRAPHY.heading_font,
    bodySize: designTemp.typography?.font_size_body ?? DEFAULT_TYPOGRAPHY.font_size_body,
    headingSize: designTemp.typography?.font_size_heading ?? DEFAULT_TYPOGRAPHY.font_size_heading,
    lineHeight: designTemp.typography?.line_height ?? DEFAULT_TYPOGRAPHY.line_height,
  };

  return (
    <Form>
      {/* ── Logo ──────────────────────────────────────────────────── */}
      <h6 className="mb-3">
        Logo
        {hasFieldChanged(designTemp.images?.logo, originalDesignTemp.images?.logo) && <ChangedIndicator />}
      </h6>

      <input
        type="file"
        ref={logoInputRef}
        onChange={handleLogoFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />

      {!readOnly && (
        <div
          onClick={() => !logoUploading && logoInputRef.current?.click()}
          onDrop={(e) => { if (!logoUploading) handleLogoDrop(e); }}
          onDragOver={handleDragOver}
          style={{
            border: "2px dashed #dee2e6",
            borderRadius: "8px",
            padding: "24px",
            textAlign: "center",
            cursor: logoUploading ? "default" : "pointer",
            marginBottom: "12px",
            transition: "border-color 0.2s",
            opacity: logoUploading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => { if (!logoUploading) e.currentTarget.style.borderColor = "#0049ff"; }}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#dee2e6")}
        >
          {logoUploading ? (
            <div>
              <Spinner animation="border" size="sm" className="text-muted" />
              <div className="text-muted mt-2" style={{ fontSize: "0.8rem" }}>Uploading…</div>
            </div>
          ) : designTemp.images?.logo ? (
            <div>
              <img
                src={designTemp.images.logo}
                alt="Logo"
                style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
              />
              <div className="text-muted mt-2" style={{ fontSize: "0.8rem" }}>
                Click to change
              </div>
            </div>
          ) : (
            <div>
              <i className="bi bi-cloud-arrow-up" style={{ fontSize: "2rem", color: "#6c757d" }}></i>
              <div className="text-muted mt-1">Click or drag a logo</div>
              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                PNG, JPG &bull; max 2MB
              </div>
            </div>
          )}
        </div>
      )}
      {logoUploadError && (
        <div className="text-danger mb-2" style={{ fontSize: "0.8rem" }}>
          <i className="bi bi-exclamation-circle me-1"></i>{logoUploadError}
        </div>
      )}

      {readOnly && designTemp.images?.logo && (
        <div className="mb-3">
          <img
            src={designTemp.images.logo}
            alt="Logo"
            style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
          />
        </div>
      )}

      {designTemp.images?.logo && !readOnly && (
        <div className="mb-4 text-end">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => updateDesignTemp({ images: { ...rawDesignTemp.images, logo: undefined } })}
            disabled={logoUploading}
          >
            <i className="bi bi-trash me-1"></i>Remove logo
          </Button>
        </div>
      )}

      {/* ── Email Preview ─────────────────────────────────────────── */}
      <hr />
      <h6 className="mb-3 text-muted" style={{ fontSize: "0.85rem" }}>
        Preview
      </h6>

      <div
        className="mb-4"
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Header — secondary background */}
        <div
          style={{
            backgroundColor: ec.secondary.bg,
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {designTemp.images?.logo ? (
            <img src={designTemp.images.logo} alt="logo" style={{ maxHeight: 28, maxWidth: 100, objectFit: "contain" }} />
          ) : (
            <span
              style={{
                color: ec.secondary.text,
                fontFamily: et.headingFont,
                fontWeight: 600,
                fontSize: 15,
                opacity: 0.5,
                fontStyle: "italic",
              }}
            >
              Your Logo
            </span>
          )}
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            style={{
              marginLeft: "auto",
              color: ec.secondary.link,
              fontFamily: et.bodyFont,
              fontSize: 12,
              textDecoration: ec.secondary.linksUnderlined ? "underline" : "none",
            }}
          >
            View in browser
          </a>
        </div>

        {/* Body — primary background */}
        <div style={{ backgroundColor: ec.primary.bg, padding: "24px 20px" }}>
          <div
            style={{
              fontFamily: et.headingFont,
              fontSize: et.headingSize,
              fontWeight: 700,
              color: ec.primary.text,
              lineHeight: et.lineHeight,
              marginBottom: 10,
            }}
          >
            Dear Jan Jansen,
          </div>
          <p
            style={{
              fontFamily: et.bodyFont,
              fontSize: et.bodySize,
              color: ec.primary.text,
              lineHeight: et.lineHeight,
              margin: "0 0 16px",
            }}
          >
            We have an update for you about your property search.{" "}
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{ color: ec.primary.link, textDecoration: ec.primary.linksUnderlined ? "underline" : "none" }}
            >
              Click here to view the details
            </a>
            .
          </p>

          {/* Accent callout block */}
          <div
            style={{
              backgroundColor: ec.accent.bg,
              color: ec.accent.text,
              padding: "12px 16px",
              borderRadius: 6,
              fontFamily: et.bodyFont,
              fontSize: et.bodySize,
              lineHeight: et.lineHeight,
              marginBottom: 20,
            }}
          >
            <strong>New match found</strong> — a property matching your criteria is now available.{" "}
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{ color: ec.accent.link, textDecoration: ec.accent.linksUnderlined ? "underline" : "none" }}
            >
              View listing
            </a>
          </div>

          {/* CTA button — secondary background */}
          <div>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{
                display: "inline-block",
                backgroundColor: ec.secondary.bg,
                color: ec.secondary.text,
                fontFamily: et.bodyFont,
                fontSize: et.bodySize,
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: 5,
                textDecoration: "none",
              }}
            >
              View My Properties
            </a>
          </div>
        </div>

        {/* Footer — secondary background */}
        <div
          style={{
            backgroundColor: ec.secondary.bg,
            padding: "10px 20px",
            textAlign: "center",
            fontFamily: et.bodyFont,
            fontSize: 11,
            color: ec.secondary.text,
          }}
        >
          Your Real Estate Agency &bull; Street 123, 1000 Brussels
        </div>
      </div>

      <hr />

      {/* ── Colors ────────────────────────────────────────────────── */}
      <h6 className="mb-3">
        Colors
        {hasFieldChanged(designTemp.colors, originalDesignTemp.colors) && <ChangedIndicator />}
      </h6>

      {COLOR_GROUPS.map((group) => (
        <div key={group.key} className="mb-4">
          <div className="mb-2">
            <Form.Label className="fw-semibold mb-0" style={{ fontSize: "0.85rem" }}>
              {group.label}
            </Form.Label>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              {group.description}
            </div>
          </div>
          <Row>
            {COLOR_FIELDS.map((field) => {
              const colorValue = (designTemp.colors?.[group.key]?.[field.key] as string) || "";
              const originalValue = (originalDesignTemp.colors?.[group.key]?.[field.key] as string) || "";
              const placeholder = DEFAULT_COLORS[group.key][field.key] as string;
              return (
                <Col md={3} key={field.key} className="mb-2">
                  <Form.Label style={{ fontSize: "0.8rem" }}>
                    {field.label}
                    {hasFieldChanged(colorValue, originalValue) && <ChangedIndicator />}
                  </Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="color"
                      value={colorValue || placeholder}
                      onChange={(e) => updateColor(group.key, field.key, e.target.value)}
                      disabled={readOnly}
                      style={{
                        width: "40px",
                        height: "38px",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        cursor: readOnly ? "default" : "pointer",
                        padding: "2px",
                        flexShrink: 0,
                      }}
                    />
                    <Form.Control
                      type="text"
                      value={colorValue}
                      onChange={(e) => updateColor(group.key, field.key, e.target.value)}
                      placeholder={placeholder}
                      readOnly={readOnly}
                      style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                    />
                  </div>
                </Col>
              );
            })}
            <Col md={3} className="mb-2 d-flex align-items-end pb-1">
              <Form.Check
                type="switch"
                id={`links-underlined-${group.key}`}
                checked={designTemp.colors?.[group.key]?.links_underlined ?? true}
                onChange={(e) => updateColor(group.key, "links_underlined", e.target.checked)}
                disabled={readOnly}
                label={
                  <span style={{ fontSize: "0.8rem" }}>
                    Links underlined
                    {hasFieldChanged(
                      designTemp.colors?.[group.key]?.links_underlined,
                      originalDesignTemp.colors?.[group.key]?.links_underlined,
                    ) && <ChangedIndicator />}
                  </span>
                }
              />
            </Col>
          </Row>
        </div>
      ))}

      <hr />

      {/* ── Typography ────────────────────────────────────────────── */}
      <h6 className="mb-3">
        Typography
        {hasFieldChanged(designTemp.typography, originalDesignTemp.typography) && <ChangedIndicator />}
      </h6>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.85rem" }}>
              Body Font
              {hasFieldChanged(designTemp.typography?.font_family, originalDesignTemp.typography?.font_family) && (
                <ChangedIndicator />
              )}
            </Form.Label>
            <FontSelect
              value={designTemp.typography?.font_family || ""}
              onChange={(v) => updateTypography("font_family", v)}
              disabled={readOnly}
              placeholder={`-- Select (${DEFAULT_TYPOGRAPHY.font_family.split(",")[0]}) --`}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.85rem" }}>
              Heading Font
              {hasFieldChanged(designTemp.typography?.heading_font, originalDesignTemp.typography?.heading_font) && (
                <ChangedIndicator />
              )}
            </Form.Label>
            <FontSelect
              value={designTemp.typography?.heading_font || ""}
              onChange={(v) => updateTypography("heading_font", v)}
              disabled={readOnly}
              placeholder={`-- Select (${DEFAULT_TYPOGRAPHY.heading_font.split(",")[0]}) --`}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <Form.Label style={{ fontSize: "0.85rem" }}>
            Body Font Size — {et.bodySize}px
            {hasFieldChanged(
              designTemp.typography?.font_size_body,
              originalDesignTemp.typography?.font_size_body,
            ) && <ChangedIndicator />}
          </Form.Label>
          <input
            type="range"
            className="form-range"
            min={10}
            max={20}
            step={1}
            value={et.bodySize}
            onChange={(e) => updateTypography("font_size_body", Number(e.target.value))}
            disabled={readOnly}
          />
          <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.7rem" }}>
            <span>10px</span><span>20px</span>
          </div>
        </Col>
        <Col md={4}>
          <Form.Label style={{ fontSize: "0.85rem" }}>
            Heading Font Size — {et.headingSize}px
            {hasFieldChanged(
              designTemp.typography?.font_size_heading,
              originalDesignTemp.typography?.font_size_heading,
            ) && <ChangedIndicator />}
          </Form.Label>
          <input
            type="range"
            className="form-range"
            min={16}
            max={36}
            step={1}
            value={et.headingSize}
            onChange={(e) => updateTypography("font_size_heading", Number(e.target.value))}
            disabled={readOnly}
          />
          <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.7rem" }}>
            <span>16px</span><span>36px</span>
          </div>
        </Col>
        <Col md={4}>
          <Form.Label style={{ fontSize: "0.85rem" }}>
            Line Height — {et.lineHeight}
            {hasFieldChanged(designTemp.typography?.line_height, originalDesignTemp.typography?.line_height) && (
              <ChangedIndicator />
            )}
          </Form.Label>
          <input
            type="range"
            className="form-range"
            min={10}
            max={25}
            step={1}
            value={Math.round(et.lineHeight * 10)}
            onChange={(e) => updateTypography("line_height", Number(e.target.value) / 10)}
            disabled={readOnly}
          />
          <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.7rem" }}>
            <span>1.0</span><span>2.5</span>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

export default CompanyBranding;
